<template>
  <div class="user-management">
    <div class="user-management__header">
      <h1 class="user-management__title">User Management</h1>
      <p class="user-management__seats">{{ seatsUsed }} of {{ seatsTotal }} seats used</p>
    </div>

    <v-form class="user-management__invite" @submit.prevent="sendInvite">
      <v-text-field
        v-model="inviteEmail"
        label="Invite by email"
        type="email"
        density="comfortable"
        hide-details="auto"
        class="user-management__invite-field" />
      <v-btn color="primary" type="submit" :loading="inviting" :disabled="seatsFull || !inviteEmail">
        Send Invite
      </v-btn>
    </v-form>

    <p v-if="seatsFull" class="user-management__seats-warning">
      All seats are in use. Revoke a pending invite or contact Lamar Health to add seats.
    </p>

    <v-alert v-if="successMessage" type="success" variant="tonal" class="mb-4">{{ successMessage }}</v-alert>
    <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
      <div v-for="error in errors" :key="error">{{ error }}</div>
    </v-alert>

    <v-data-table :headers="headers" :items="users" :loading="loading" item-value="id" class="user-management__table">
      <template #[`item.name`]="{ item }">{{ fullName(item) }}</template>
      <template #[`item.isAdmin`]="{ item }">
        <v-chip size="small" :color="item.isAdmin ? 'primary' : undefined" variant="tonal">
          {{ item.isAdmin ? "Admin" : "Member" }}
        </v-chip>
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip size="small" :color="item.status === 'active' ? 'success' : 'warning'" variant="tonal">
          {{ item.status === "active" ? "Active" : "Pending" }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn v-if="item.status === 'pending'" size="small" variant="text" color="error" @click="promptRevoke(item)">
          Revoke
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="confirmRevokeOpen" max-width="420">
      <v-card>
        <v-card-title>Revoke invite</v-card-title>
        <v-card-text> Revoke the pending invite for {{ revokeTarget?.email }}? </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmRevokeOpen = false">Cancel</v-btn>
          <v-btn color="error" :loading="revoking" @click="confirmRevoke">Revoke</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores";
import usersService from "@/services/usersService";
import { tryParseApiErrors } from "@/utils";

const router = useRouter();
const authStore = useAuthStore();

const users = ref([]);
const seatsUsed = ref(0);
const seatsTotal = ref(0);
const loading = ref(false);
const inviteEmail = ref("");
const inviting = ref(false);
const errors = ref([]);
const successMessage = ref("");

const confirmRevokeOpen = ref(false);
const revokeTarget = ref(null);
const revoking = ref(false);

const headers = [
  { title: "Name", key: "name" },
  { title: "Email", key: "email" },
  { title: "Role", key: "isAdmin" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions", sortable: false },
];

const seatsFull = computed(() => seatsTotal.value > 0 && seatsUsed.value >= seatsTotal.value);

const fullName = (user) => `${user.firstName} ${user.lastName}`.trim() || "—";

const loadUsers = async () => {
  loading.value = true;
  try {
    const { results, seatsUsed: used, seatsTotal: total } = await usersService.listUsers();
    users.value = results;
    seatsUsed.value = used;
    seatsTotal.value = total;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    loading.value = false;
  }
};

const sendInvite = async () => {
  inviting.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    await usersService.inviteUser(inviteEmail.value);
    successMessage.value = `Invite sent to ${inviteEmail.value}.`;
    inviteEmail.value = "";
    await loadUsers();
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    inviting.value = false;
  }
};

const promptRevoke = (user) => {
  revokeTarget.value = user;
  confirmRevokeOpen.value = true;
};

const confirmRevoke = async () => {
  revoking.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    await usersService.revokeInvite(revokeTarget.value.id);
    successMessage.value = `Invite for ${revokeTarget.value.email} revoked.`;
    confirmRevokeOpen.value = false;
    await loadUsers();
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    revoking.value = false;
  }
};

onMounted(() => {
  if (!authStore.userInfo.isAdmin) {
    router.replace({ name: "home" });
    return;
  }
  loadUsers();
});
</script>

<style lang="scss" scoped>
.user-management {
  padding: 24px 32px;
}

.user-management__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 24px;
}

.user-management__title {
  font-family: "DM Sans", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #052e5e;
}

.user-management__seats {
  color: #5a6b85;
  font-weight: 500;
}

.user-management__invite {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.user-management__invite-field {
  max-width: 360px;
}

.user-management__seats-warning {
  color: #b26a00;
  margin-bottom: 16px;
}

.user-management__table {
  margin-top: 8px;
}
</style>
